import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { useWizard } from "./WizardContext";

export function FieldShell({ id, label, required, error, hint, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`} data-error={error ? "true" : undefined}>
      <label htmlFor={id} className="block text-sm font-medium text-[#2C2424]">
        {label} {required && <span className="text-[#A31828]">*</span>}
      </label>
      {children}
      {hint && !error && <p className="text-xs text-[#8C6A6C]">{hint}</p>}
      {error && <p data-testid={`error-${id}`} className="text-xs font-medium text-[#A31828]">{error}</p>}
    </div>
  );
}

const useField = (section, name) => {
  const { data, set, errors } = useWizard();
  const id = `${section}-${name}`;
  return { id, value: data[section][name] ?? "", onChange: (v) => set(section, name, v), error: errors[`${section}.${name}`] };
};

const errCls = (e) => (e ? "border-[#A31828]" : "");

export function TextField({ section, name, label, required, hint, className, ...props }) {
  const f = useField(section, name);
  return (
    <FieldShell id={f.id} label={label} required={required} error={f.error} hint={hint} className={className}>
      <Input id={f.id} data-testid={`field-${f.id}`} value={f.value} onChange={(e) => f.onChange(e.target.value)} className={`rmm-input h-10 bg-white ${errCls(f.error)}`} {...props} />
    </FieldShell>
  );
}

export function SelectField({ section, name, label, required, options, hint, className, disabled }) {
  const f = useField(section, name);
  return (
    <FieldShell id={f.id} label={label} required={required} error={f.error} hint={hint} className={className}>
      <select id={f.id} data-testid={`field-${f.id}`} value={f.value} disabled={disabled} onChange={(e) => f.onChange(e.target.value)} className={`rmm-select rmm-input ${errCls(f.error)}`}>
        <option value="">Select...</option>
        {options.map((o) => {
          const v = typeof o === "string" ? o : o.value;
          return <option key={v} value={v}>{typeof o === "string" ? o : o.label}</option>;
        })}
      </select>
    </FieldShell>
  );
}

export function TextAreaField({ section, name, label, required, hint, className, rows = 3, ...props }) {
  const f = useField(section, name);
  return (
    <FieldShell id={f.id} label={label} required={required} error={f.error} hint={hint} className={className}>
      <Textarea id={f.id} rows={rows} data-testid={`field-${f.id}`} value={f.value} onChange={(e) => f.onChange(e.target.value)} className={`rmm-input bg-white ${errCls(f.error)}`} {...props} />
    </FieldShell>
  );
}

// "NA" by default; reveals a details field when applicable (manglik, physically challenged)
export function NAToggleField({ name, metaKey, label, className = "" }) {
  const { data, set, errors } = useWizard();
  const yes = data.meta[metaKey];
  const choose = (v) => {
    set("meta", metaKey, v);
    set("candidate", name, v ? "" : "NA");
  };
  const pill = (active) => `rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${active ? "border-[#7A0016] bg-[#7A0016] text-[#F5E6BA]" : "border-[#D4AF37]/50 bg-white text-[#2C2424] hover:bg-[#F5E6BA]/40"}`;
  return (
    <FieldShell id={`candidate-${name}`} label={label} error={errors[`candidate.${name}`]} className={className}>
      <div className="flex gap-2">
        <button type="button" data-testid={`field-candidate-${name}-na`} onClick={() => choose(false)} className={pill(!yes)}>NA</button>
        <button type="button" data-testid={`field-candidate-${name}-yes`} onClick={() => choose(true)} className={pill(yes)}>Applicable</button>
      </div>
      {yes && (
        <Input data-testid={`field-candidate-${name}`} placeholder="Please give details" value={data.candidate[name] === "NA" ? "" : data.candidate[name]} onChange={(e) => set("candidate", name, e.target.value)} className="rmm-input mt-2 h-10 bg-white" />
      )}
    </FieldShell>
  );
}

export const Grid = ({ children }) => <div className="grid gap-5 sm:grid-cols-2">{children}</div>;
export const SubHeading = ({ children }) => (
  <h3 className="col-span-full mt-2 font-display text-xl font-semibold text-[#7A0016]">{children}</h3>
);
